// src/components/copilot/blocks/TableBlock.js
import { useState } from "react";
import { WorkspaceLink } from "../../../hooks/useWsNavigate";
import { ArrowUpRight, ChevronLeft, ChevronRight, Download } from "lucide-react";

function csvEscape(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const HIDDEN_COLUMNS = ["batch_id"]; // helper columns used only to build links
const PAGE_SIZE = 12;

// A production "call" is a conversation; runs link via their batch_id.
function linkForCell(colName, value, columns, row) {
  if (value === null || value === undefined) return null;
  if (colName === "call_id") return `/observability/conversations/${value}`;
  if (colName === "run_id") {
    const bi = columns.indexOf("batch_id");
    const batchId = bi >= 0 ? row[bi] : null;
    return batchId != null ? `/batch-run/${batchId}/run/${value}` : null;
  }
  return null;
}

const isIdCol = (name) => name.endsWith("_id");
const numericRe = /^-?\d+(\.\d+)?$/;

function isNumericValue(name, value) {
  return !isIdCol(name) && value != null && numericRe.test(String(value));
}

function formatCell(name, value) {
  if (value === null || value === undefined) return "—";
  const s = String(value);
  if (isIdCol(name)) return s; // ids stay raw
  if (numericRe.test(s)) {
    const n = Number(s);
    const v = Number.isInteger(n) ? n : Math.round(n * 100) / 100;
    return v.toLocaleString();
  }
  return s;
}

export default function TableBlock({ block }) {
  const columns = block.columns || [];
  const rows = block.rows || [];
  const [page, setPage] = useState(0);

  const visibleCols = columns
    .map((c, idx) => ({ name: c, idx }))
    .filter(({ name }) => !HIDDEN_COLUMNS.includes(name));

  // Right-align a column if its first non-empty value is numeric.
  const alignRight = {};
  visibleCols.forEach(({ name, idx }) => {
    const sample = rows.find((r) => r[idx] !== null && r[idx] !== undefined);
    alignRight[name] = sample ? isNumericValue(name, sample[idx]) : false;
  });

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);

  // Export the FULL result set (all rows, visible columns) as CSV.
  function downloadCsv() {
    const header = visibleCols.map(({ name }) => name);
    const body = rows.map((row) => visibleCols.map(({ idx }) => row[idx]));
    const csv = [header, ...body].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(block.title || "copilot-data").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-foreground">{block.title || "Results"}</span>
          {block.source && (
            <span
              title={`Data source: ${block.source}`}
              className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground whitespace-nowrap"
            >
              {block.source}
            </span>
          )}
        </div>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={downloadCsv}
            title="Download CSV"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
            CSV
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 bg-muted/60 backdrop-blur">
            <tr>
              {visibleCols.map(({ name }) => (
                <th
                  key={name}
                  className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground ${
                    alignRight[name] ? "text-right" : "text-left"
                  }`}
                >
                  {name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, i) => (
              <tr
                key={start + i}
                className={`border-t border-border/70 transition-colors hover:bg-muted/40 ${
                  i % 2 === 1 ? "bg-muted/20" : ""
                }`}
              >
                {visibleCols.map(({ name, idx }) => {
                  const cell = row[idx];
                  const to = linkForCell(name, cell, columns, row);
                  return (
                    <td
                      key={name}
                      className={`px-4 py-2 tabular-nums ${alignRight[name] ? "text-right" : "text-left"} text-foreground`}
                    >
                      {to ? (
                        <WorkspaceLink to={to} className="inline-flex items-center gap-1 text-primary font-medium hover:underline">
                          {formatCell(name, cell)}
                          <ArrowUpRight className="h-3.5 w-3.5 opacity-70" strokeWidth={2} />
                        </WorkspaceLink>
                      ) : (
                        formatCell(name, cell)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer: row count + pagination */}
      <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
        <span>
          {rows.length === 0
            ? "No rows"
            : `Showing ${start + 1}–${Math.min(start + PAGE_SIZE, rows.length)} of ${rows.length.toLocaleString()}`}
        </span>
        {totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} /> Prev
            </button>
            <span className="px-1">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={safePage >= totalPages - 1}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
